/**
 * `agentToolNames.ts` — port of Unity's `AI/AgentToolNames.cs`.
 *
 * Wire ids must match the Unity `AgentToolNamesExtensions.ToolIdMap`
 * **exactly** (mixed snake_case / Pascal_Snake is intentional) so prompts,
 * recorded actions, and MCP clients stay compatible across platforms.
 *
 * Only the subset with web executors is listed in `WEB_PORTED_TOOLS`; the
 * full map is kept so future slices can light tools up without re-deriving
 * ids.
 */

export const AgentToolNames = {
  // Entities
  MoveParts: "move_parts",
  MoveRelative: "move_relative",
  HideParts: "hide_parts",
  UnhideParts: "unhide_parts",
  Isolate: "isolate",
  ClearIsolation: "clear_isolation",
  ResetPlacement: "reset_placement",
  ResetScene: "Reset_Scene",
  SelectPart: "select_part",
  FocusOnPart: "focus_on_part",
  LookAtPart: "look_at",
  HighlightPart: "highlight_part",
  ClearHighlight: "clear_highlight",
  ConstantSpinPart: "constant_spin",
  ClearConstantSpin: "clear_constant_spin",
  UpdatePartContext: "update_part_context",
  RenamePart: "rename_part",
  ToggleModelAnimation: "Toggle_Model_Animation",
  GetPartTransforms: "Get_Part_Transforms",
  // Web-first: Unity has no measured-answer tool — its Measure tool is
  // hand-driven only, so the agent had no deterministic source for a size or
  // a height above the ground.
  GetPartDimensions: "Get_Part_Dimensions",
  ClearBoundingBox: "clear_bounding_box",
  UnexplodeModel: "unexplode_model",

  // Scenes
  NewScene: "new_scene",
  DuplicateScene: "duplicate_scene",
  GoToScene: "go_to_scene",
  GoToNextScene: "Go_To_NextScene",
  GoToPreviousScene: "Go_To_PreviousScene",
  ResetSceneView: "reset_scene_view",
  RenameScene: "rename_scene",
  UpdateSceneNotes: "update_scene_notes",
  ReorderScene: "reorder_scene",
  DeleteScene: "delete_scene",
  PlayAction: "play_action",
  PauseCurrentAction: "pause_current_action",
  ContinueCurrentAction: "continue_current_action",
  ToggleActionButtonVisibility: "toggle_action_button_visibility",

  // Assets
  GetPartMaterials: "Get_Part_Materials",
  UpdateMaterialProperties: "Update_Material_properties",
  CopyModel: "copy_model",
  DeleteModel: "delete_model",
  PasteModel: "paste_model",
  GetObjectsFromLibrary: "Get_Objects_From_Library",
  GetMaterialLibrary: "Get_Material_Library",
  ApplyMaterialOnParts: "apply_material_on_parts",
  ImportFromLibrary: "Import_from_Library",

  // Annotations
  CreateAnnotation: "create_annotation",
  CreateMeasurement: "create_measurement",
  TakeSnapshot: "take_snapshot",
  GetShortestDistance: "get_shortest_distance",

  // SelectionSets
  CreateSelectionSet: "create_selection_set",
  RemoveSelectionSet: "remove_selection_set",
  // Web-first: counting is answered from selection sets rather than by the
  // agent tallying hierarchy lines, which is not deterministic.
  CountParts: "Count_Parts",

  // Project
  GetAvailableProjects: "Get_Available_Projects",
  OpenProject: "Open_Project",
  CreateNewProject: "create_new_project",
  ExitProject: "exit_project",

  // PDF
  PromptSearchDocs: "Prompt_Search_Docs",
  SearchDocByFileName: "Search_Doc_By_File_Name",
  FindAndShowPDF: "Find_and_Show_PDF",
  SavePDFPlacement: "Save_PDF_placement",
  // Web-first, and stubbed for the preview (`previewNotImplemented.ts`): the
  // web opens a document in a browser tab, so there is no viewer to land on a
  // passage in, hold one result at a time, or search inside.
  OpenDocAtReference: "Open_Doc_At_Reference",
  GoToNextDocResult: "Go_To_Next_Doc_Result",
  SearchInOpenDoc: "Search_In_Open_Doc",

  // Memory — web-first recall of the signed-in user's durable transcript.
  RecallFromMemory: "Recall_From_Memory",

  // Context
  GetProjectContext: "Get_Project_Context",
  GetSceneContext: "Get_Scene_Context",
  GetPartContext: "Get_Part_Context",
  PromptSceneContextAgent: "Prompt_SceneContextAgent",
  TakeScreenshot: "take_screenshot",

  // AIBehaviour
  SwitchRole: "switch_role",
  LoadSkill: "Load_Skill",
  ChangePersonality: "Change_personality",
  ResetConversation: "reset_conversation",
  Undo: "undo",
  UseConstantListening: "Use_ConstantListening",
  ChangeWakeGesture: "Change_wake_gesture",
  ToggleGuide: "toggle_play_mode",
  ListenUser: "Listen_User",
  SendUserText: "Send_User_Text",
  SetVerbatium: "Set_Verbatium",
  SaveRecordingAsAction: "save_recording_as_action",
  ToggleAIPanel: "Toggle_AIPanel",
  // Web-first: Unity's `AIPanel` is one surface with no separate message
  // container, so there is nothing upstream to mirror. Shows or hides the
  // CONVERSATION inside the Spark panel — the chat/transcript the user reads —
  // which is a different surface from `Toggle_AIPanel`'s minimal-panel mode.
  ToggleTranscript: "Toggle_Transcript",
  ToggleDebugMode: "Toggle_DebugMode",
  ToggleSpark: "Toggle_Spark",
  StandHere: "stand_here",
  StandWhereverYouWant: "stand_wherever_you_want",
  PresentOptions: "present_options",
  WaitFor: "wait_for",
  // Script-only: starts the owning step's video. Not registered as a live tool.
  PlayVideo: "play_video",

  // Documents (unported — id kept so the core-allowed tool set matches Unity)
  CreateMdFile: "create_md_file",

  // Workflow
  GetWorkflow: "get_workflow",
  // Web-first: the desktop client has no workflow-schema tool to mirror.
  GetWorkflowSchema: "get_workflow_schema",
  UpdateWorkflow: "update_workflow",
  PlayWorkflowScript: "play_workflow_script",
  // Web-first: Unity's guide has no step-navigation tool — the learner's panel
  // was the only way through a run.
  GoToWorkflowStep: "go_to_workflow_step",
  // Web-first, and deliberately NOT Unity's `toggle_play_mode` (kept above as
  // `ToggleGuide`): that id is core-always-allowed, so registering it would
  // hand EVERY role both halves of play mode — the Guide included. The halves
  // are two ids so each can be granted on its own. This one ENDS a run; the
  // Guide holds it, and so do the author roles.
  StopWorkflow: "stop_workflow",
  // Web-first, the other half: STARTS a run exactly as the Play button does, so
  // an author can say "start preview" (AI-1357). Held by the roles an author is
  // in while nothing plays (`assistant`, `workflow-builder`) and never by the
  // Guide, which leads a run that is already playing and must not start one.
  StartWorkflow: "start_workflow",
  // Web-first: the desktop client has no step media at all, so there is nothing
  // upstream to mirror. Controls the video the RUNNING step is showing — the
  // Guide's half of the media panel's own playback cluster.
  ControlMedia: "control_media",
  // Web-first: the desktop client has no workflow outline panel. Shows or hides
  // the outline — the Groups/Steps map of the workflow — which the learner's run
  // header and the editor panel both toggle.
  ToggleWorkflowOutline: "Toggle_Workflow_Outline",
  // Web-first: reads the live camera so a step's `step_view` can be authored
  // from it. Filed with the workflow family because that is its only use — the
  // desktop client captures a scene view from a button and needs no tool.
  GetCameraView: "get_camera_view",
  // Legacy slot, unregistered: scripts now live inside the workflow file.
  CreateSceneScript: "create_scene_script",

  // Legacy / internal / recording
  ChangeColor: "change_color",
  ResetColors: "reset_colors",
  Explode: "explode",
  ClippingPlane: "clipping_plane",
  ToggleTraining: "Toggle_Training",
  GetPartInfoAlias: "Get_Part_Info",
  DoNotWait: "do_not_wait",
} as const;

export type AgentToolName = (typeof AgentToolNames)[keyof typeof AgentToolNames];
